import { useRef, useState } from 'react';
import { Images, Loader2 } from 'lucide-react';
import { api, formatApiError, thumbSrc } from '@/lib/api';
import { useContent } from '@/lib/content';

const inputCls = 'bg-ink border border-white/15 px-3 py-2.5 text-sm text-cream placeholder:text-white/30 focus:border-champagne focus:outline-none transition-colors';

export const BulkUpload = ({ onDone }) => {
  const { categories } = useContent();
  const fileRef = useRef(null);
  const [category, setCategory] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [uploaded, setUploaded] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!category) {
      setError('Choose a category first, then pick photos.');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setBusy(true);
    setError('');
    setSaved(false);
    const created = [];
    try {
      for (let i = 0; i < files.length; i++) {
        setProgress(`Uploading ${i + 1} of ${files.length} — ${files[i].name}`);
        const fd = new FormData();
        fd.append('file', files[i]);
        const up = await api.post('/upload', fd);
        const title = files[i].name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ');
        const { data } = await api.post('/admin/portfolio', {
          title, category, type: 'image', image: up.data.url, caption: '', location: '', description: '', order: 100, visible: true,
        });
        created.push(data);
      }
      setUploaded(created);
      onDone?.();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
      setProgress('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const saveCaptions = async () => {
    setSaving(true);
    setError('');
    try {
      for (const item of uploaded) {
        await api.put(`/admin/portfolio/${item.id}`, item);
      }
      setUploaded([]);
      setSaved(true);
      onDone?.();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="bulk-upload" className="mb-8 border border-white/10 bg-surface p-5 sm:p-6">
      <p className="text-[10px] uppercase tracking-[0.25em] text-champagne mb-4">Bulk Photo Upload</p>
      <div className="flex flex-col sm:flex-row gap-3">
        <select
          data-testid="bulk-category"
          className={`${inputCls} cursor-pointer sm:w-64`}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="" className="bg-ink">Choose category…</option>
          {(categories || []).map((c) => <option key={c.id} value={c.label} className="bg-ink">{c.label}</option>)}
        </select>
        <button
          type="button"
          data-testid="bulk-choose-files"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="inline-flex items-center justify-center gap-2 border border-champagne/60 text-champagne px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] hover:bg-champagne hover:text-ink transition-colors disabled:opacity-60"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Images size={14} />}
          {busy ? 'Uploading…' : 'Choose Photos (multiple)'}
        </button>
        <input
          ref={fileRef}
          data-testid="bulk-file-input"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={handleFiles}
        />
      </div>
      {progress && <p data-testid="bulk-progress" className="mt-3 text-sm text-champagne">{progress}</p>}
      {error && <p className="mt-3 text-sm text-red-400">{error}</p>}
      {saved && <p data-testid="bulk-saved" className="mt-3 text-sm text-champagne">Captions saved — photos are live.</p>}

      {uploaded.length > 0 && (
        <div className="mt-6">
          <p className="text-white/50 text-sm mb-4">{uploaded.length} photo{uploaded.length === 1 ? '' : 's'} uploaded to {category}. Add captions, then save:</p>
          <div className="space-y-3">
            {uploaded.map((item, i) => (
              <div key={item.id} data-testid={`bulk-item-${i}`} className="flex items-center gap-4 border border-white/10 p-3">
                <img src={thumbSrc(item.image)} alt="" className="w-16 h-12 object-cover shrink-0" />
                <div className="flex-1 min-w-0 grid sm:grid-cols-2 gap-3">
                  <input
                    data-testid={`bulk-title-${i}`}
                    className={inputCls}
                    value={item.title}
                    onChange={(e) => setUploaded(uploaded.map((u, idx) => (idx === i ? { ...u, title: e.target.value } : u)))}
                    placeholder="Title"
                  />
                  <input
                    data-testid={`bulk-caption-${i}`}
                    className={inputCls}
                    value={item.caption || ''}
                    onChange={(e) => setUploaded(uploaded.map((u, idx) => (idx === i ? { ...u, caption: e.target.value } : u)))}
                    placeholder="Caption (shown under the photo)"
                  />
                </div>
              </div>
            ))}
          </div>
          <button
            data-testid="bulk-save-captions"
            onClick={saveCaptions}
            disabled={saving}
            className="mt-5 inline-flex items-center gap-2 bg-champagne text-ink px-6 py-3 text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-cream transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save Captions
          </button>
        </div>
      )}
    </div>
  );
};
